import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";

import { Icon } from "../../components/Icon";
import { Callout, Field, Pill, Section, ToggleRow, errText, relativeTime, useToast } from "../ui";
import {
  api,
  type AdminClientDetail,
  type AdminTicketSyncConfig,
  type StatusDisplayEntry,
} from "../../services/api";

/**
 * Ticket sync.
 *
 * The Autotask write surface is double-gated (global master switch AND this
 * per-company config), so every write-side control here states which gate is
 * holding it rather than just rendering grey. Reads are the only thing that
 * works today for most customers, and the copy says so.
 *
 * Status display maps an Autotask status id onto one of the four labels a
 * portal user understands. The colour that rides with each label is fixed per
 * label so the portal reads consistently across customers.
 */

/** The portal-facing status vocabulary, each pinned to a design-system token. */
const PORTAL_LABELS: { label: string; color: string }[] = [
  { label: "Open", color: "badge-progress" },
  { label: "In progress", color: "badge-active" },
  { label: "Waiting on you", color: "badge-waiting" },
  { label: "Closed", color: "badge-closed" },
];

export function SyncSection({
  client,
  onChanged,
}: {
  client: AdminClientDetail;
  onChanged: () => void;
}) {
  const toast = useToast();
  const { data, mutate, isLoading, error: loadError } = useSWR<AdminTicketSyncConfig>(
    ["/api/admin/clients", client.id, "ticket-sync-config"],
    () => api.admin.clients.ticketSync.get(client.id),
  );

  const [readEnabled, setReadEnabled] = useState(true);
  const [writeEnabled, setWriteEnabled] = useState(false);
  const [syncNotes, setSyncNotes] = useState(true);
  const [clientCanAddNotes, setClientCanAddNotes] = useState(false);
  const [queueId, setQueueId] = useState<string>("");
  const [priorityId, setPriorityId] = useState<string>("");
  const [mapping, setMapping] = useState<Record<string, StatusDisplayEntry>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setReadEnabled(data.read_enabled);
    setWriteEnabled(data.write_enabled);
    setSyncNotes(data.sync_notes);
    setClientCanAddNotes(data.client_can_add_notes);
    setQueueId(data.default_queue_id != null ? String(data.default_queue_id) : "");
    setPriorityId(data.default_priority_id != null ? String(data.default_priority_id) : "");
    setMapping(data.status_display_mapping);
    setSavedAt(data.updated_at);
  }, [data]);

  const dirty = useMemo(() => {
    if (!data) return false;
    return (
      readEnabled !== data.read_enabled ||
      writeEnabled !== data.write_enabled ||
      syncNotes !== data.sync_notes ||
      clientCanAddNotes !== data.client_can_add_notes ||
      queueId !== (data.default_queue_id != null ? String(data.default_queue_id) : "") ||
      priorityId !==
        (data.default_priority_id != null ? String(data.default_priority_id) : "") ||
      JSON.stringify(mapping) !== JSON.stringify(data.status_display_mapping)
    );
  }, [
    data,
    readEnabled,
    writeEnabled,
    syncNotes,
    clientCanAddNotes,
    queueId,
    priorityId,
    mapping,
  ]);

  function reset() {
    if (!data) return;
    setReadEnabled(data.read_enabled);
    setWriteEnabled(data.write_enabled);
    setSyncNotes(data.sync_notes);
    setClientCanAddNotes(data.client_can_add_notes);
    setQueueId(data.default_queue_id != null ? String(data.default_queue_id) : "");
    setPriorityId(data.default_priority_id != null ? String(data.default_priority_id) : "");
    setMapping(data.status_display_mapping);
    setSaveError(null);
  }

  async function save() {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await api.admin.clients.ticketSync.update(client.id, {
        read_enabled: readEnabled,
        write_enabled: writeEnabled,
        sync_notes: syncNotes,
        client_can_add_notes: clientCanAddNotes,
        default_queue_id: queueId ? Number(queueId) : null,
        default_priority_id: priorityId ? Number(priorityId) : null,
        status_display_mapping: mapping,
      });
      // Re-seed from the server: write_enabled can come back false even when we
      // asked for true, because the global gate has the last word.
      await mutate(updated, { revalidate: false });
      onChanged();
      setSavedAt(new Date().toISOString());
      toast(
        updated.forced_off.includes("write_enabled")
          ? "Saved — write-back stays off until the global Autotask flag is on"
          : "Saved Ticket sync",
        updated.forced_off.includes("write_enabled") ? "warn" : "ok",
      );
    } catch (e) {
      setSaveError(errText(e, "Save failed."));
    } finally {
      setSaving(false);
    }
  }

  if (loadError) {
    return (
      <div className="card form-error">
        Failed to load ticket-sync config:{" "}
        {String((loadError as Error).message ?? loadError)}
      </div>
    );
  }

  const statuses = data?.available_statuses ?? [];
  const unmapped = statuses.filter((s) => !(String(s.value) in mapping));
  const writeGloballyOff = data ? !data.write_globally_enabled : true;
  const noteCreateUnsupported = data ? !data.note_create_supported : true;

  return (
    <Section
      title="Ticket sync"
      short="sync"
      desc={`How ${client.name}'s Autotask tickets appear in the portal, and how new ones are created.`}
      dirty={dirty}
      saving={saving}
      error={saveError}
      savedLabel={relativeTime(savedAt)}
      onSave={save}
      onDiscard={reset}
    >
      {client.setup_health?.sync_refused && (
        <div style={{ marginBottom: 14 }}>
          <Callout kind="warn">
            <strong>Ticket sync is refused.</strong> {client.setup_health.sync_refused}, so
            no tickets sync for this customer until its Autotask link is fixed.
          </Callout>
        </div>
      )}
      <ToggleRow
        label="Show Autotask tickets"
        sub={
          client.autotask_company_id != null
            ? `Tickets for company AT ${client.autotask_company_id} appear under Tickets.`
            : "This customer has no Autotask company linked, so there is nothing to show."
        }
        checked={readEnabled}
        disabled={isLoading}
        onChange={setReadEnabled}
      />
      <ToggleRow
        label="Create tickets in Autotask from the portal"
        sub={
          writeGloballyOff ? (
            <>
              Held off globally while the Autotask credentials are read-only.{" "}
              <Pill kind="warn">Global flag off</Pill>
            </>
          ) : (
            "Portal-raised tickets are written straight into Autotask. The same switch lets clients reopen, resolve, or close a ticket, and a customer note or attachment moves it to Customer Note Added."
          )
        }
        checked={writeEnabled}
        disabled={isLoading}
        onChange={setWriteEnabled}
      />
      <ToggleRow
        label="Show technician notes"
        sub="Non-internal Autotask notes appear in the ticket thread."
        checked={syncNotes}
        disabled={isLoading}
        onChange={setSyncNotes}
      />
      <ToggleRow
        label="Let users reply on tickets"
        sub={
          noteCreateUnsupported ? (
            <>
              Needs the Autotask note API. <Pill kind="warn">No write surface yet</Pill>
            </>
          ) : (
            "Portal users can add notes and attachments to their own tickets."
          )
        }
        checked={clientCanAddNotes}
        disabled={isLoading || noteCreateUnsupported}
        onChange={setClientCanAddNotes}
      />
      <div className="ad-grid" style={{ marginTop: 20 }}>
        <Field label="Default queue for new tickets">
          <select
            value={queueId}
            disabled={isLoading}
            onChange={(e) => setQueueId(e.target.value)}
          >
            <option value="">— choose —</option>
            {(data?.available_queues ?? []).map((q) => (
              <option key={q.value} value={String(q.value)}>
                {q.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Default priority">
          <select
            value={priorityId}
            disabled={isLoading}
            onChange={(e) => setPriorityId(e.target.value)}
          >
            <option value="">— choose —</option>
            {(data?.available_priorities ?? []).map((p) => (
              <option key={p.value} value={String(p.value)}>
                {p.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="ad-eyebrow" style={{ margin: "22px 0 10px" }}>
        Status display
      </div>

      {statuses.length === 0 ? (
        <Callout>
          Autotask status picklists have not been synced yet, so there is nothing to map.
          Run an Autotask sync to populate them.
        </Callout>
      ) : (
        <>
          {Object.keys(mapping).length === 0 && (
            <div className="ad-note" style={{ marginBottom: 10 }}>
              No overrides. Portal users see the raw Autotask status until you add one.
            </div>
          )}
          <div className="ad-mapping">
            {Object.entries(mapping).map(([statusId, entry]) => {
              const known = statuses.find((s) => String(s.value) === statusId);
              return (
                <span key={statusId} style={{ display: "contents" }}>
                  <span className="from">{known ? known.label : `Status ${statusId}`}</span>
                  <Icon name="arrowRight" size={14} />
                  <select
                    value={entry.label}
                    aria-label={`Portal label for ${known?.label ?? statusId}`}
                    onChange={(e) => {
                      const chosen = PORTAL_LABELS.find((l) => l.label === e.target.value);
                      if (!chosen) return;
                      setMapping((m) => ({
                        ...m,
                        [statusId]: { label: chosen.label, color: chosen.color },
                      }));
                    }}
                  >
                    {!PORTAL_LABELS.some((l) => l.label === entry.label) && (
                      <option value={entry.label}>{entry.label}</option>
                    )}
                    {PORTAL_LABELS.map((l) => (
                      <option key={l.label} value={l.label}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="rm"
                    aria-label={`Remove the ${known?.label ?? statusId} mapping`}
                    onClick={() =>
                      setMapping((m) => {
                        const next = { ...m };
                        delete next[statusId];
                        return next;
                      })
                    }
                  >
                    <Icon name="x" size={13} />
                  </button>
                </span>
              );
            })}
          </div>

          {unmapped.length > 0 && (
            <div style={{ marginTop: 12, maxWidth: 260 }}>
              <Field label="Add a status">
                <select
                  value=""
                  onChange={(e) => {
                    if (!e.target.value) return;
                    setMapping((m) => ({
                      ...m,
                      [e.target.value]: {
                        label: PORTAL_LABELS[0].label,
                        color: PORTAL_LABELS[0].color,
                      },
                    }));
                  }}
                >
                  <option value="">— choose an Autotask status —</option>
                  {unmapped.map((s) => (
                    <option key={s.value} value={String(s.value)}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          )}
        </>
      )}
    </Section>
  );
}
